'use client';

/**
 * CookieBanner — minimal stub (COMP-03).
 *
 * REMOS uses ZERO third-party tracking / analytics. The only cookies set are:
 *   - sb-* (Supabase Auth session cookies — Art. 6 (1) f / legitimate interest,
 *     no consent needed per ePrivacy "strictly necessary" carve-out)
 *   - cookie-banner-dismissed (this banner's own dismissal state)
 *
 * Banner gated by localStorage so it appears once per visitor. No analytics
 * opt-in toggles needed — there is nothing to opt out of.
 *
 * Plan 015-02 Task 4.
 */
import { Cookie, X } from 'lucide-react';
import Link from 'next/link';
import { useState, useSyncExternalStore } from 'react';

const DISMISSED_KEY = 'cookie-banner-dismissed';

function subscribe(callback: () => void) {
  // 'storage' fires only when ANOTHER tab modifies localStorage. For same-tab
  // we manage state via React's own setState (see dismiss()), so we just
  // return an empty unsubscribe.
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
}

function getSnapshot() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1' ? 'dismissed' : 'visible';
  } catch {
    return 'dismissed'; // localStorage blocked → don't spam
  }
}

function getServerSnapshot() {
  return 'dismissed' as const; // never render banner SSR (avoid hydration mismatch)
}

export function CookieBanner() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  // Local same-tab dismissal so we don't have to round-trip through storage events.
  const [dismissedLocal, setDismissedLocal] = useState(false);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // ignore — UI just shows again next visit
    }
    setDismissedLocal(true);
  };

  const show = state === 'visible' && !dismissedLocal;
  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-label="Datenschutz-Hinweis"
      // data-theme-preset: the banner renders in the root layout, OUTSIDE the
      // persona/preset wrappers, so its semantic tokens (bg-background/border)
      // would otherwise resolve to the neutral light fallback even on the dark
      // admin — a jarring bright box. Opting it into the darkmatter cascade
      // makes it follow the active light/dark mode like the rest of the app.
      data-theme-preset="darkmatter"
      className="border-border bg-background fixed inset-x-4 bottom-4 z-50 mx-auto max-w-2xl rounded-md border p-4 shadow-lg"
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <Cookie className="text-primary mt-0.5 size-5 shrink-0" aria-hidden />
        <div className="flex-1 text-sm">
          {/* Explicit text-foreground: the banner renders in the root layout
              with only `data-theme-preset="darkmatter"` for its tokens. Without
              an explicit color the heading inherits the OUTER (light-fallback)
              foreground — dark navy on the dark `bg-background` → effectively
              invisible (axe color-contrast 1.05). text-foreground binds it to
              the preset's --foreground so it flips with light/dark like the
              muted paragraph below. */}
          <p className="text-foreground font-medium">Datenschutz-Hinweis</p>
          <p className="text-muted-foreground mt-1 text-xs">
            REMOS verwendet ausschließlich technisch notwendige Cookies (Login-Session). Es findet
            kein Tracking, keine Werbe-Cookies und keine Übermittlung an Drittanbieter statt.{' '}
            <Link
              href="/datenschutz"
              className="text-primary underline underline-offset-2 hover:underline"
              data-testid="cookie-banner-datenschutz-link"
            >
              Mehr in der Datenschutzerklärung.
            </Link>
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md"
          aria-label="Hinweis schließen"
          data-testid="cookie-banner-dismiss"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
